import React from 'react'
import FrostOverlay from './FrostOverlay'

interface ListingCardProps {
  title: string
  price: string
  address: string
  beds: number
  baths: number
  sqft: number
  imageAlt: string
  imageUrl: string
  linkText: string
  linkUrl: string
  attribution?: string
  /** Status ribbon label (F.5): "NEW" / "PENDING" / "UNDER CONTRACT" / "SOLD" etc. */
  ribbon?: string
  /** gold = gold pill (NEW) · ink = ink pill w/ gold text (PENDING/UNDER CONTRACT) · muted = grey (SOLD/OFF MARKET) */
  ribbonTone?: 'gold' | 'ink' | 'muted'
}

const ListingCard: React.FC<ListingCardProps> = ({
  title,
  price,
  address,
  beds,
  baths,
  sqft,
  imageAlt,
  imageUrl,
  linkText,
  linkUrl,
  attribution = 'Courtesy of Jack Lingo - Lewes',
  ribbon,
  ribbonTone = 'ink',
}) => {
  const ribbonClass =
    ribbonTone === 'gold'
      ? 'bg-accent-gold text-primary-ink'
      : ribbonTone === 'muted'
        ? 'bg-white/15 text-white/85'
        : 'bg-primary-ink text-accent-gold border border-accent-gold/50'
  return (
    <article
      className="group relative overflow-hidden bg-primary-ink"
      aria-label={`Property: ${title}, Price: ${price}`}
    >
      {/* Whole card is the link — OG cards carry no per-card button */}
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2 focus:ring-offset-neutral-charcoal"
        aria-label={`${linkText}: ${address}, ${price}`}
      >
        {/* Photo fills entire card */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            loading="lazy"
          />

          {/* Dark gradient overlay, bottom 40% */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

          {/* Frosted brand hover overlay (A.4) — floating touch chip suppressed;
              touch devices get the CTA strip below the photo instead so it can
              never overlay the price/address text */}
          <FrostOverlay cta={linkText} hideTouchCta />

          {/* Status ribbon (F.5), top-right — attribution owns top-left */}
          {ribbon && (
            <p
              className={`absolute top-3 right-4 px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] rounded-[3px] ${ribbonClass}`}
            >
              {ribbon}
            </p>
          )}

          {/* Brokerage attribution — compliance microtext, top-left */}
          <p className="absolute top-3 left-4 text-[9px] font-sans uppercase tracking-[0.15em] text-white/60">
            {attribution}
          </p>

          {/* Bottom-left overlay stack: price → 28×2 gold bar → address.
              z-30 keeps the details crisp above the FrostOverlay (z-10/z-20)
              so they read clearly on the hover overlay. */}
          <div className="absolute bottom-0 left-0 z-30 p-6">
            <p className="text-[20px] font-bold text-white mb-2">{price}</p>
            <div className="w-[28px] h-[2px] bg-accent-gold mb-3" />
            <p className="font-display text-[12px] font-medium tracking-[0.2em] uppercase text-white/90 leading-tight">
              {address}
            </p>

            {/* Beds/Baths/SQFT — slash-separated uppercase microtext: visible on mobile, hover-reveal on desktop */}
            <p className="mt-2 text-[10px] font-display font-medium tracking-[0.15em] uppercase text-white/70 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300">
              {beds} BD / {baths} BA / {sqft.toLocaleString()} SQFT
            </p>
          </div>
        </div>

        {/* Touch-only CTA strip below the photo — the floating corner chip was
            overlaying the price/address text, so on hover-less devices the CTA
            lives here instead (desktop keeps the centered hover chip) */}
        <span className="hidden [@media(hover:none)]:flex items-center justify-between bg-primary-ink px-4 py-3">
          <span className="font-display text-[11px] font-medium uppercase tracking-[0.2em] text-accent-gold">
            {linkText}
          </span>
          <span className="text-base leading-none text-accent-gold" aria-hidden="true">
            +
          </span>
        </span>
      </a>
    </article>
  )
}

export default ListingCard
